import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import Dropdown from "@/ui/molecules/Dropdown";
import { Button, Icon, IconBox } from "@proda-ai/ui-kit";

export const testIds = {
    main: "CoaSelectPrompt",
};

export type CoaOption = { coaId: number; name: string };

/**
 * The CoA picker's view state, derived once (see `deriveCoaState`) and shared by
 * both upload flows so they can't disagree:
 * - `not_applicable`: no prompt — not an OS upload, the project already has a
 *   user-chosen CoA, or the CoA is locked by an existing OS extraction.
 * - `loading`: a check is still in flight. Renders nothing — Continue is just
 *   disabled, so there is no loading flicker.
 * - `error`: a check failed. Shows the error with a Retry.
 * - `ready`: the project needs a CoA. The dropdown renders only when `required`
 *   (more than one permitted CoA); a lone option is assigned silently.
 */
export type CoaSelectState =
    | { kind: "not_applicable" }
    | { kind: "loading" }
    | { kind: "error"; onRetry: () => void }
    | {
          kind: "ready";
          options: CoaOption[];
          selectedCoaId: number | null;
          onSelect: (coaId: number) => void;
          onRetry: () => void;
          /** More than one permitted CoA — the user must pick one. */
          required: boolean;
          /** An assign is in flight — block re-submission until it settles. */
          assigning: boolean;
      };

/** Continue must stay blocked until a CoA is loaded and selected, and while an
 * assign is already in flight. */
export const coaStateBlocksContinue = (state: CoaSelectState): boolean =>
    state.kind === "loading" ||
    state.kind === "error" ||
    (state.kind === "ready" &&
        (state.selectedCoaId === null || state.assigning));

/**
 * Whether the CoA gate needs something from the user before an extraction can
 * proceed: a Retry after a failed check, or a choice between several permitted
 * CoAs. Everything else (loading, sole option, no prompt) resolves on its own.
 */
export const coaStateRequiresUserInput = (state: CoaSelectState): boolean =>
    state.kind === "error" || (state.kind === "ready" && state.required);

export type CoaSelectPromptProps = { state: CoaSelectState };

/**
 * CoA picker shown before an Operating Statement upload. Renders only when the
 * user actually has something to do (see `coaStateRequiresUserInput`): an error
 * with a Retry, or a dropdown when a choice is required. It owns no Continue
 * button so the surrounding step controls it (gating on
 * `coaStateBlocksContinue`). Mirrors the proda-core-ui rendering.
 */
export const CoaSelectPrompt = ({ state }: CoaSelectPromptProps) => {
    // Same condition as `coaStateRequiresUserInput`, spelled out so TypeScript
    // narrows `state` to the two rendered variants.
    if (state.kind !== "error" && state.kind !== "ready") return null;
    if (state.kind === "ready" && !state.required) return null;

    return (
        <Box data-testid={testIds.main} sx={{ margin: "0 32px 32px 32px" }}>
            <Typography
                variant="h4"
                sx={{
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                Chart of Accounts
                <IconBox mb="auto" ml={0.5}>
                    <Icon icon="asterisk" />
                </IconBox>
            </Typography>
            {state.kind === "error" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography
                        variant="body3"
                        sx={{ color: "var(--proda-red-120)" }}
                    >
                        Unable to load the Chart of Accounts.
                    </Typography>
                    <Button
                        pattern="secondary"
                        size="small"
                        onClick={state.onRetry}
                        style={{ width: "88px", flexShrink: 0 }}
                    >
                        Retry
                    </Button>
                </Box>
            ) : (
                <Dropdown
                    hideLabel={true}
                    disabled={state.assigning}
                    options={state.options.map(coa => ({
                        label: coa.name,
                        value: coa.coaId,
                    }))}
                    value={state.selectedCoaId}
                    onChange={state.onSelect}
                />
            )}
        </Box>
    );
};
